import React, { useEffect, useState } from 'react';
import { dashboardAPI } from '@/api/api';
import { Users, BookOpen, Calendar, AlertCircle, Clock } from 'lucide-react';

const TeacherDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await dashboardAPI.getTeacherDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Error fetching teacher dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-8 rounded-xl shadow-lg">
        <h1 className="text-3xl font-bold">Welcome, {data?.teacher_info?.name}!</h1>
        <p className="text-purple-100 mt-1">
          {data?.teacher_info?.designation} | Employee ID: {data?.teacher_info?.employee_id}
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={BookOpen}
          title="My Classes"
          value={data?.classes?.length || 0}
          bgColor="bg-blue-500"
        />
        <StatCard
          icon={Users}
          title="Total Students"
          value={data?.students_count || 0}
          bgColor="bg-green-500"
        />
        <StatCard
          icon={AlertCircle}
          title="Attendance Pending"
          value={data?.attendance_pending?.length || 0}
          bgColor="bg-orange-500"
        />
      </div>

      {/* Attendance Pending Alert */}
      {data?.attendance_pending && data.attendance_pending.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-orange-600 mt-1" size={20} />
            <div>
              <h3 className="text-sm font-semibold text-orange-800 mb-2">
                ⚠️ Attendance Not Marked Today
              </h3>
              <ul className="text-sm text-orange-700 space-y-1">
                {data.attendance_pending.map((cls, index) => (
                  <li key={index}>• {cls.class_name}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Today's Schedule */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">Today's Schedule</h2>
        </div>
        
        {data?.today_schedule && data.today_schedule.length > 0 ? (
          <div className="space-y-3">
            {data.today_schedule.map((schedule, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 text-blue-600">
                  <Clock size={18} />
                  <span className="font-semibold text-sm">
                    {schedule.start_time} - {schedule.end_time}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">{schedule.subject_name}</p>
                  <p className="text-sm text-gray-600">{schedule.class_name}</p>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  {schedule.period_number ? `Period ${schedule.period_number}` : 'Scheduled'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Calendar className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No classes scheduled for today</p>
          </div>
        )}
      </div>

      {/* My Classes */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <BookOpen className="text-blue-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-800">My Classes</h2>
        </div>
        
        {data?.classes && data.classes.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.classes.map((cls) => (
              <div key={cls.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <BookOpen className="text-blue-600" size={24} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{cls.name}</p>
                    <p className="text-sm text-gray-600">Class Teacher</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <BookOpen className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No classes assigned yet</p>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickActionButton icon={Users} label="Mark Attendance" />
          <QuickActionButton icon={BookOpen} label="View Timetable" />
          <QuickActionButton icon={Calendar} label="Schedule Meeting" />
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, bgColor }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-gray-600 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-gray-800 mt-2">{value}</p>
      </div>
      <div className={`${bgColor} p-4 rounded-full`}>
        <Icon className="text-white" size={28} />
      </div>
    </div>
  </div>
);

const QuickActionButton = ({ icon: Icon, label }) => (
  <button className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors">
    <Icon className="text-blue-600" size={20} />
    <span className="font-medium text-gray-700">{label}</span>
  </button>
);

export default TeacherDashboard;
